import React from 'react';
import { useTranslation } from 'react-i18next';
import { cva } from 'class-variance-authority';
import { cn } from '../lib/utils';

const buttonVariants = cva(
    "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
    {
        variants: {
            variant: {
                default: "bg-primary text-primary-foreground hover:bg-primary/90",
                ghost: "hover:bg-accent hover:text-accent-foreground",
                nav: "hover:text-bg hover:bg-main hover:rounded-3xl transition-all",
            },
            size: {
                default: "h-10 px-4 py-2",
                sm: "h-9 rounded-md px-3",
                xs: "h-8 rounded-md px-2",
            },
        },
        defaultVariants: {
            variant: "ghost",
            size: "sm",
        },
    }
);

interface LanguageSwitcherProps {
    variant?: "default" | "compact";
}

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ variant = "default" }) => {
    const { i18n } = useTranslation();

    const changeLanguage = (lng: string) => {
        i18n.changeLanguage(lng);
    };

    // Use compact styling for navbar
    if (variant === "compact") {
        return (
            <div className="flex items-center">
                <select
                    className="appearance-none bg-transparent border-none text-sm focus:ring-0 focus:outline-none cursor-pointer"
                    value={i18n.language}
                    onChange={(event) => changeLanguage(event.target.value)}
                    aria-label="Select language"
                    style={{
                        backgroundImage: 'none',
                        WebkitAppearance: 'none',
                        MozAppearance: 'none',
                    }}
                >
                    <option value="en">🇬🇧 EN</option>
                    <option value="da">🇩🇰 DA</option>
                </select>
            </div>
        );
    }

    // Default styled buttons
    return (
        <div className="flex items-center gap-2">
            <button
                className={cn(
                    buttonVariants(),
                    i18n.language === 'en' ? 'bg-accent text-accent-foreground' : ''
                )}
                onClick={() => changeLanguage('en')}
                aria-label="Switch to English"
            >
                🇬🇧 EN
            </button>
            <button
                className={cn(
                    buttonVariants(),
                    i18n.language === 'da' ? 'bg-accent text-accent-foreground' : ''
                )}
                onClick={() => changeLanguage('da')}
                aria-label="Switch to Danish"
            >
                🇩🇰 DA
            </button>
        </div>
    );
};

export default LanguageSwitcher; 